import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

// Actions
import { cancelAppliedFilters } from "../../../actions/filter/FilterActions";
import {
  cancelSiteMonitoring,
  createSetupMonitoring,
  getMonitoringCampaignSummary,
  getMonitoringCitySummary,
  openSetupMonitoringForm,
  resetMediaSitesSelection,
} from "../../../actions/campaign-monitoring/MonitoringSettingsActions";

// Constants and Utils
import {
  DefaultPagination,
  FormDataTargets,
} from "../../../constants/GeneralConstants";
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";
import { checkIsAdinnSpecific } from "../../../common-utils/user-utils/userUtils";

// Url
import { RedirectTo } from "../../../urls/PageLinksURL";

// Components
import { Dropdown } from "../../../components/dropdown/Dropdown";
import Spinner from "../../../components/spinner/Spinner";
import Filter from "../../../components/filter/Filter";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import MediaSetupMonitoringForm from "../../../components/media-setup-monitoring-form/MediaSetupMonitoringForm";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";

// Pages and Sections
import MonitoringCampaignCityViewTable from "./MonitoringCampaignCityViewTable";

// CSS
import "../CampaignMonitoring.scss";

// Page Action Section
function PageActionsSection({ routeChange = () => {} }) {
  // check org specific
  const isAdinnSpecific = checkIsAdinnSpecific();

  // hiding upload sites button for Adinn org
  if (isAdinnSpecific) {
    return null;
  }

  // upload sites button
  return (
    <button className="btn btn-primary" onClick={routeChange}>
      {"Upload Sites"}
    </button>
  );
}

// Page Components
function PageHeaderSection({ campaign, citySelected, campaignType }) {
  const { id, cityId } = useParams();
  const history = useHistory();

  // route-change on excel-upload
  const routeChange = () => {
    history.push({
      pathname: constructRedirectPath(RedirectTo.monitorCampaignCreatePageUrl),
      state: { campaignId: id, cityId: cityId, exCampaignType: campaignType },
    });
  };

  const pageTitle = campaign.title + " :: " + citySelected.cityName;
  const pageActions = <PageActionsSection routeChange={routeChange} />;

  return <PageHeader title={pageTitle} actions={pageActions} />;
}

function MonitorSelectAction({ cancelSiteMonitoringFunction }) {
  const dispatch = useDispatch();
  const createMonitoringLoading = useSelector(
    (state) => state.monitoringSettings.setupMonitoringLoading
  );
  const cancelSiteMonitoringLoading = useSelector(
    (state) => state.monitoringSettings.cancelSiteMonitoringLoading
  );

  function openSetupMonitoringFormFunction() {
    dispatch(openSetupMonitoringForm(true));
  }

  return (
    <div className="d-flex mt-1">
      <Dropdown
        buttonName="Select action"
        style="btn btn-outline-primary m-0 mr-3"
      >
        <div className="dropdown-menu dropdown-menu-left">
          <button
            className="dropdown-item"
            type="button"
            data-toggle="modal"
            data-target={`#${FormDataTargets.setupMonitoringForm}`}
            onClick={openSetupMonitoringFormFunction}
          >
            {"Setup monitoring"}
          </button>
          <button
            className="dropdown-item"
            type="button"
            onClick={() => cancelSiteMonitoringFunction()}
          >
            {"Cancel monitoring"}
          </button>
        </div>
      </Dropdown>

      {/* Loading */}
      {(createMonitoringLoading || cancelSiteMonitoringLoading) && (
        <AlertMessage
          className="mb-0 py-1"
          spinnerClassName="spinner-border-sm"
          textMessage={"Please Wait.."}
        />
      )}
    </div>
  );
}

/**
 * Main Page
 */
function MonitoringCampaignCityViewPage() {
  const dispatch = useDispatch();
  const { id, cityId } = useParams();

  // Selector state
  //  monitoring city-summary
  const citySummaryLoading = useSelector(
    (state) => state.monitoringSettings.monitoringCitySummaryLoading
  );
  const monitoringCitySummary =
    useSelector((state) => state.monitoringSettings.monitoringCitySummary) ||
    {};
  const citySummaryItems = monitoringCitySummary.items || [];
  const pagination = monitoringCitySummary.pagination || {};

  // monitoring campaign summary
  // for "total MediaCount" and "CampaignTitle"
  const campaignSummaryLoading = useSelector(
    (state) => state.monitoringSettings.campaignSummaryLoading
  );
  const monitoringCampaignSummary =
    useSelector((state) => state.monitoringSettings.campaignSummary) || {};
  const { campaign } = monitoringCampaignSummary || {};
  const { infoByCity, campaignType } = campaign || {};
  const citySummary = infoByCity?.[cityId] || {};
  const citySelected = {
    cityId: cityId,
    cityName: citySummary.cityName || cityId,
  };

  // for Setup-Monitoring Popup
  const openSetupMonitoringForm = useSelector(
    (state) => state.monitoringSettings.openModal
  );

  // Link to pass in CampaignSidebar
  const redirectLinkObj = {
    campaignOverviewLink: RedirectTo.monitorCampaignViewPageUrl.replace(
      ":campaignId",
      id
    ),
    citySummaryLink: RedirectTo.monitorCityViewPageUrl.replace(
      ":campaignId",
      id
    ),
  };

  // Selected MediaIds( From Checkbox-multiSelect ) For SetupMonitoring (Array of MediaIdsCSV)
  const selectedSitesObject = useSelector(
    (state) => state.monitoringSettings.selectedSites
  );
  const identifiersKeys = Object.keys(selectedSitesObject);
  const checkedMediaIds = identifiersKeys.filter(
    (id) => selectedSitesObject[id]
  );

  // Edit Site Monitoring ( single select)
  const monitoringMediaId = useSelector(
    (state) => state.monitoringSettings.monitoringMediaId
  );

  const monitoringEnabledIds = useSelector(
    (state) => state.monitoringSettings.monitoringEnabledIds
  );

  const allMonSettingsEnabledMediaIds = useSelector(
    (state) => state.monitoringSettings.allMonSettingsEnabledMediaIds
  );

  const editMonitoringMedia = useSelector(
    (state) => state.monitoringSettings.editMonitoringMedia
  );

  // Final checkedMediaIds or mediaId for Create/edit setup-monitoring-settings
  const finalMediaIds = monitoringMediaId
    ? [monitoringMediaId]
    : checkedMediaIds;

  // Pagination
  const { pageNumber, pageSize } = DefaultPagination;

  useEffect(() => {
    dispatch(getMonitoringCampaignSummary(id));
    dispatch(getMonitoringCitySummary(id, cityId, pageNumber, pageSize));
    dispatch(cancelAppliedFilters());
    dispatch(resetMediaSitesSelection());
  }, [dispatch, id, cityId, pageSize]);

  // Functions
  function loadCityMedia(pageNumber, pageSize) {
    dispatch(
      getMonitoringCitySummary(id, cityId, pageNumber, pageSize, "", true)
    );
  }

  // Create Monitoring Settings Function
  function onSubmitMonitoringSettings(inputFieldBean) {
    dispatch(createSetupMonitoring(inputFieldBean, id, cityId, finalMediaIds));
  }

  function cancelSiteMonitoringFunction() {
    // Logic for settings Ids of both checked and enabled from the citySummaryItems
    const checkedMediaIdsSet = new Set(checkedMediaIds);
    const allMonSettingsEnabledMediaIdsSet = new Set(
      allMonSettingsEnabledMediaIds
    );

    const monEnabledCheckedSettingsIds = citySummaryItems
      .filter((item) => {
        const mediaId = item.media.id;
        return (
          checkedMediaIdsSet.has(mediaId) &&
          allMonSettingsEnabledMediaIdsSet.has(mediaId)
        );
      })
      .map((item) => item.settings?.id);

    const monSettingIds =
      checkedMediaIds.length > 0
        ? monEnabledCheckedSettingsIds
        : monitoringEnabledIds;

    if (monSettingIds.length > 0) {
      dispatch(cancelSiteMonitoring(id, cityId, monSettingIds));
    }
  }

  // Page-loading
  if (citySummaryLoading || campaignSummaryLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      <CampaignSidebar
        campaign={campaign}
        citySelected={citySelected}
        redirectLinkObj={redirectLinkObj}
      />

      {/* Page-Content */}
      <div className="content-wrapper">
        {/* Page-Header */}
        <PageHeaderSection
          campaign={campaign}
          citySelected={citySelected}
          campaignType={campaignType}
        />

        <div className="page-content">
          {/* Action and Filter Section */}
          <div className="d-flex justify-content-between">
            <MonitorSelectAction
              cancelSiteMonitoringFunction={cancelSiteMonitoringFunction}
            />
            <Filter onFiltersApplied={loadCityMedia} pageSize={pageSize} />
          </div>

          {/* monitoring city summary table */}
          <MonitoringCampaignCityViewTable
            citySummaryItems={citySummaryItems}
            pagination={pagination}
            loadCityMedia={loadCityMedia}
          />
        </div>
      </div>

      {/* modals */}
      {openSetupMonitoringForm && (
        <MediaSetupMonitoringForm
          editMonitoringMedia={editMonitoringMedia}
          onFormSubmit={onSubmitMonitoringSettings}
        />
      )}
    </>
  );
}

export default MonitoringCampaignCityViewPage;
